/**
 * Pick a single underlying integration source for a fitness-labeled taxonomy
 * field, based on the gym's installed/active integrations.
 *
 * Background: the API model stores one key per condition rule today. The
 * Paper "Add criteria menu spec" (node `GL1-0`) describes a longer-term
 * compile-to-$or fan-out, but until the API supports it we resolve a single
 * source at write time so the persisted ListRule shape stays unchanged.
 */
import type { Integration, IntegrationProviderId } from "@/api/types";
import { IntegrationType } from "@/api/types";
import type {
  TaxonomyEventField,
  TaxonomyEventSource,
  TaxonomyField,
  TaxonomyPropertyField,
  TaxonomyPropertySource,
  Vertical,
} from "./segment-taxonomy";

/**
 * Providers that put a gym in the nightlife vertical. Today only Tablelist.
 */
const NIGHTLIFE_PROVIDERS: IntegrationProviderId[] = ["tablelist"];

/**
 * Fitness studio-management platforms. Their sync is what backs the member and
 * prospect analytics (memberships, intro offers, booking funnel), so those
 * views are only meaningful for gyms with one of these active.
 */
const FITNESS_PROVIDERS: IntegrationProviderId[] = ["mariana-tek", "mindbody", "fitdegree"];

/**
 * Spa and wellness platforms. Today only Zenoti.
 */
const WELLNESS_PROVIDERS: IntegrationProviderId[] = ["zenoti"];

/**
 * True when the gym has an active fitness studio-management integration
 * (Mariana Tek, Mindbody, or fitDEGREE) — the source of member/prospect data.
 */
export function hasActiveFitnessIntegration(
  installedIntegrations: Integration[] | undefined,
): boolean {
  const active = activeProviderIds(installedIntegrations);
  return FITNESS_PROVIDERS.some((provider) => active.has(provider));
}

/**
 * True when the gym has an active integration that feeds Pulse: any fitness
 * platform or Zenoti. Analytics stay fitness-only; Pulse runs a detector set
 * per vertical.
 */
export function hasActivePulseIntegration(
  installedIntegrations: Integration[] | undefined,
): boolean {
  const active = activeProviderIds(installedIntegrations);
  return [...FITNESS_PROVIDERS, ...WELLNESS_PROVIDERS].some((provider) => active.has(provider));
}

/**
 * Choose the curated vertical from the gym's installed integrations. An
 * active Tablelist integration means nightlife, an active Zenoti integration
 * means wellness, and everyone else (Mariana Tek, Mindbody, fitDEGREE, …)
 * defaults to fitness.
 */
export function resolveVertical(installedIntegrations: Integration[] | undefined): Vertical {
  const active = activeProviderIds(installedIntegrations);
  if (NIGHTLIFE_PROVIDERS.some((provider) => active.has(provider))) {
    return "nightlife";
  }
  if (WELLNESS_PROVIDERS.some((provider) => active.has(provider))) {
    return "wellness";
  }
  return "fitness";
}

/**
 * Preference order applied when a field is backed by multiple integration
 * sources but the gym has more than one of them installed. Earlier entries
 * win. Helios (built-ins like email/phone) is special-cased at the bottom
 * because we always have it.
 */
const PROVIDER_PREFERENCE: IntegrationProviderId[] = [
  "mariana-tek",
  "mindbody",
  "fitdegree",
  "zenoti",
  "tablelist",
  "hubspot",
  "active-campaign",
  "activecampaign",
  "shopify",
  "square",
  "gymble",
  "webhook",
  "helios-pixel",
  "helios",
];

/**
 * Returns the providers (`IntegrationProviderId`) the gym has installed
 * and currently active. Helios is always considered active because it
 * backs the base customer fields (email, phone, firstName, lastName).
 * Website events (`helios-pixel`) are always active because every gym
 * has Helios forms, and Pages when published.
 */
export function activeProviderIds(
  installedIntegrations: Integration[] | undefined,
): Set<IntegrationProviderId> {
  const set = new Set<IntegrationProviderId>(["helios", "helios-pixel"]);
  for (const integration of installedIntegrations ?? []) {
    if (!integration.active) continue;
    // `IntegrationType` enum values are the `IntegrationProviderId` strings.
    set.add(integration.type as unknown as IntegrationProviderId);
  }
  return set;
}

/**
 * Pick the gym's primary CRM provider for displaying system-asset
 * iconography (system segments, flows, message templates).
 *
 * System lists/flows/templates don't store which integration they belong to,
 * so we resolve the icon from the gym's installed integrations. Returns the
 * highest-preference active provider, ignoring Helios (which is always
 * "installed" but backs built-in fields rather than an external CRM).
 *
 * Falls back to `"helios"` to preserve the historical default when a gym
 * has no external CRM installed.
 */
export function pickPrimaryProvider(
  installedIntegrations: Integration[] | undefined,
): IntegrationProviderId {
  const active = activeProviderIds(installedIntegrations);
  for (const provider of PROVIDER_PREFERENCE) {
    if (provider === "helios" || provider === "helios-pixel") continue;
    if (active.has(provider)) return provider;
  }
  return "helios";
}

interface PickOptions {
  /**
   * Set of customer-property keys observed in the API's sampled
   * `available-properties` response. When provided, a source whose key is
   * present in this set is preferred over one that isn't — even if the
   * preferred-order provider technically supports it.
   */
  sampledPropertyKeys?: Set<string>;
  /**
   * Set of `customer-event.event` names observed in the API's sampled
   * `available-events` response. Same preference logic as
   * `sampledPropertyKeys` but for event-kind fields.
   */
  sampledEventNames?: Set<string>;
}

export interface PickedPropertySource {
  provider: IntegrationProviderId;
  source: TaxonomyPropertySource;
}

export interface PickedEventSource {
  provider: IntegrationProviderId;
  source: TaxonomyEventSource;
}

/**
 * Pick the underlying source for a property-kind field given the gym's
 * installed integrations.
 *
 * Returns `null` when none of the field's declared sources are installed
 * (so the caller can omit the field from the menu).
 */
export function pickPropertySource(
  field: TaxonomyPropertyField,
  installedIntegrations: Integration[] | undefined,
  options: PickOptions = {},
): PickedPropertySource | null {
  const active = activeProviderIds(installedIntegrations);
  const candidates = listCandidates(field.sources, active);
  if (candidates.length === 0) return null;

  // Prefer installed providers whose key was actually observed in the
  // sampled property metadata. Falls back to the first installed provider
  // in the configured preference order.
  if (options.sampledPropertyKeys?.size) {
    const sampled = candidates.find((candidate) =>
      options.sampledPropertyKeys?.has(candidate.source.key),
    );
    if (sampled) return sampled;
  }
  return candidates[0] ?? null;
}

/**
 * Pick the underlying source for an event-kind field given the gym's
 * installed integrations.
 */
export function pickEventSource(
  field: TaxonomyEventField,
  installedIntegrations: Integration[] | undefined,
  options: PickOptions = {},
): PickedEventSource | null {
  const active = activeProviderIds(installedIntegrations);
  const candidates = listCandidates(field.sources, active);
  if (candidates.length === 0) return null;

  if (options.sampledEventNames?.size) {
    const sampled = candidates.find((candidate) =>
      options.sampledEventNames?.has(candidate.source.event),
    );
    if (sampled) return sampled;
  }
  return candidates[0] ?? null;
}

/**
 * Convenience overload: picks the right helper based on `field.kind`.
 * Useful when the caller has a generic `TaxonomyField`.
 */
export function pickActiveSource(
  field: TaxonomyField,
  installedIntegrations: Integration[] | undefined,
  options: PickOptions = {},
): PickedPropertySource | PickedEventSource | null {
  if (field.kind === "property") {
    return pickPropertySource(field, installedIntegrations, options);
  }
  return pickEventSource(field, installedIntegrations, options);
}

/**
 * Returns true when at least one of the field's declared sources is backed
 * by an installed integration. Used by the menu to hide unsupported rows.
 */
export function fieldHasInstalledSource(
  field: TaxonomyField,
  installedIntegrations: Integration[] | undefined,
): boolean {
  const active = activeProviderIds(installedIntegrations);
  return Object.keys(field.sources).some((provider) =>
    active.has(provider as IntegrationProviderId),
  );
}

function listCandidates<T extends TaxonomyPropertySource | TaxonomyEventSource>(
  sources: Partial<Record<IntegrationProviderId, T>>,
  active: Set<IntegrationProviderId>,
): Array<{ provider: IntegrationProviderId; source: T }> {
  const installed: Array<{ provider: IntegrationProviderId; source: T }> = [];
  for (const provider of PROVIDER_PREFERENCE) {
    const source = sources[provider];
    if (source && active.has(provider)) {
      installed.push({ provider, source });
    }
  }
  return installed;
}

// Re-export `IntegrationType` so test files can assert against the canonical
// enum without reaching into the API package.
export { IntegrationType };
